import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Clock,
  Wrench,
  User,
  FileText,
  Sparkles,
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  Camera,
  Settings,
  UserPlus,
  Save,
  X,
  Loader2,
  ClipboardList,
  AlertTriangle,
  Info,
  Calendar,
  Tag,
  ShieldCheck,
  Edit3,
  Flag,
  History,
  FilePlus,
  BookOpen,
  Cpu,
  Layers,
  Award,
  ChevronRight,
  ChevronDown,
  Play,
  ExternalLink,
} from "lucide-react";
import { maintenanceAPI, errorAPI, sopsAPI } from "../api";
import { useAuthStore } from "../store/authStore";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import clsx from "clsx";
import { PERMISSIONS } from "../constants/roles";
import { formatIstDate, formatIstDateTime } from "../utils/date";
import DocumentEvidenceList from "../components/DocumentEvidenceList";

const statusConfig = {
  completed: {
    label: "Completed",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200/80",
    dot: "bg-emerald-500",
  },
  in_progress: {
    label: "In Progress",
    badge: "bg-amber-50 text-amber-700 border-amber-200/80",
    dot: "bg-amber-500",
  },
  scheduled: {
    label: "Scheduled",
    badge: "bg-blue-50 text-blue-700 border-blue-200/80",
    dot: "bg-blue-500",
  },
  overdue: {
    label: "Overdue",
    badge: "bg-rose-50 text-rose-700 border-rose-200/80",
    dot: "bg-rose-500",
  },
  cancelled: {
    label: "Cancelled",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
  },
};

const formatPartLabel = (part) => {
  if (typeof part === "string") return part.trim();
  if (!part || typeof part !== "object") return "";

  const qty = part.qty ?? part.quantity ?? part.count ?? "";
  const label =
    part.part_number ??
    part.partNumber ??
    part.name ??
    part.part_name ??
    part.sku ??
    part.id ??
    "";

  if (!label && !qty) return "";
  if (!label) return `Qty: ${qty}`;
  if (!qty) return String(label);

  return `${label} (Qty: ${qty})`;
};

const MaintenanceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [isAssigning, setIsAssigning] = useState(false);
  const [isUpdatingSOP, setIsUpdatingSOP] = useState(false);
  const [isSopModalOpen, setIsSopModalOpen] = useState(false);
  const [selectedTech, setSelectedTech] = useState("");
  const [selectedSOP, setSelectedSOP] = useState("");
  const [selectedManualId, setSelectedManualId] = useState("");
  const [activePhoto, setActivePhoto] = useState(null);
  const [isEvidenceExpanded, setIsEvidenceExpanded] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isSopModalOpen) {
        setIsSopModalOpen(false);
      }
    };
    if (isSopModalOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSopModalOpen]);

  // Data Queries
  const {
    data: log,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["maintenance-log", id],
    queryFn: () => maintenanceAPI.getById(id).then((res) => res.data.data),
  });

  const { data: linkedBug } = useQuery({
    queryKey: ["linked-bug", log?.bug_id],
    queryFn: () => errorAPI.getById(log.bug_id).then((res) => res.data.data),
    enabled: !!log?.bug_id,
  });

  const { data: technicians } = useQuery({
    queryKey: ["technicians-list"],
    queryFn: () => maintenanceAPI.getTechnicians().then((res) => res.data.data),
    enabled:
      !!(
        user?.role === "super_admin" ||
        user?.role === "admin" ||
        user?.role === "plant_manager"
      ) || isAssigning,
  });

  const { data: availableSOPs } = useQuery({
    queryKey: ["available-sops", log?.machine_id],
    queryFn: () =>
      sopsAPI
        .getAll({ machineId: log.machine_id, limit: 100 })
        .then((res) => res.data.data),
    enabled: !!log?.machine_id && isUpdatingSOP,
  });

  const { data: machineManuals } = useQuery({
    queryKey: ["machine-manuals", log?.machine_id],
    queryFn: () =>
      maintenanceAPI
        .getMachineManuals(log.machine_id)
        .then((res) => res.data.data),
    enabled: !!log?.machine_id && isSopModalOpen,
  });

  const canManage = PERMISSIONS.MANAGE_MAINTENANCE.includes(user?.role);

  const showStandardizeButton =
    log?.status === "completed" && log?.bug_id && !log?.sop_id && canManage;

  // Mutations
  const assignMutation = useMutation({
    mutationFn: (techId) => maintenanceAPI.assign(id, techId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenance-log", id] });
      toast.success("Technician assigned successfully");
      setIsAssigning(false);
    },
    onError: (err) => toast.error("Assignment failed: " + err.message),
  });

  const generateSopMutation = useMutation({
    mutationFn: (data) => maintenanceAPI.generateSopFromFix(data),
    onSuccess: (res) => {
      toast.success("AI Draft Ready for Review");
      navigate("/sops/new", { state: { draft: res.data.data } });
    },
  });

  const updateSopMutation = useMutation({
    mutationFn: (sopId) => maintenanceAPI.updateSop(id, sopId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenance-log", id] });
      toast.success("SOP updated successfully");
      setIsUpdatingSOP(false);
    },
    onError: (err) => toast.error("Failed to update SOP: " + err.message),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center h-96 items-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-blue-500/20 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
            Loading Maintenance Details...
          </p>
        </div>
      </div>
    );
  }

  if (error || !log) {
    return (
      <div className="p-12 text-center bg-white border border-slate-200/80 rounded-2xl max-w-lg mx-auto mt-12 shadow-xs">
        <div className="w-12 h-12 bg-rose-50 text-rose-500 rounded-xl flex items-center justify-center mx-auto mb-3">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <h3 className="text-base font-bold text-slate-800">Record Not Found</h3>
        <p className="text-xs text-slate-500 mt-1 mb-5">
          This maintenance task could not be retrieved or has been removed.
        </p>
        <Link to="/maintenance" className="btn btn-secondary text-xs py-2 px-4">
          Back to Maintenance List
        </Link>
      </div>
    );
  }

  const stepsCompleted = Array.isArray(log.steps_completed)
    ? log.steps_completed
    : [];
  const sopSteps = Array.isArray(log.sop_steps) ? log.sop_steps : [];
  const partsUsed = Array.isArray(log.parts_used)
    ? log.parts_used.map(formatPartLabel).filter(Boolean)
    : [];
  const aiPlan = log.ai_plan;
  const executionSummary = log.observations?.technical_summary;
  const currentStatus = statusConfig[log.status] || statusConfig.scheduled;

  return (
    <div className="space-y-5 lg:space-y-6 w-full">
      {/* 1. Evidence Photo Lightbox */}
      <AnimatePresence>
        {activePhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/90 backdrop-blur-sm"
            onClick={() => setActivePhoto(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-5xl w-full bg-white p-2 sm:p-3 rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setActivePhoto(null)}
                className="absolute top-4 right-4 p-2 bg-white/90 backdrop-blur rounded-xl hover:bg-white text-slate-800 shadow-md z-10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <img
                src={activePhoto}
                alt="Evidence Detail"
                className="w-full h-auto max-h-[82vh] object-contain rounded-xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 2. SOP Synthesis Modal */}
      {isSopModalOpen &&
        createPortal(
          <AnimatePresence>
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
              onClick={(e) => {
                if (e.target === e.currentTarget) setIsSopModalOpen(false);
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="relative w-full max-w-xl bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col my-auto"
              >
                {/* Header */}
                <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-100 bg-white">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 border border-blue-100/80 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                        Standardize Fix as SOP
                      </h2>
                      <p className="text-xs text-slate-500 mt-0.5">
                        AI synthesis based on field execution data
                      </p>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="p-5 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto max-h-[calc(85vh-130px)]">
                  {/* Target Machine */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Target Machine
                    </label>
                    <div className="relative">
                      <Cpu className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        readOnly
                        value={log.machine_name || "Unknown Machine"}
                        className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none cursor-default shadow-xs"
                      />
                    </div>
                  </div>

                  {/* Grounding Document */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Grounding Document <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <BookOpen className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <select
                        required
                        value={selectedManualId}
                        onChange={(e) => setSelectedManualId(e.target.value)}
                        className="w-full appearance-none pl-10 pr-10 py-2.5 bg-white hover:bg-slate-50/50 border border-slate-200 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all shadow-xs cursor-pointer"
                      >
                        <option value="" disabled className="text-slate-400">
                          {machineManuals && machineManuals.length > 0
                            ? "Select a technical manual or guide..."
                            : "No manuals available for this machine"}
                        </option>
                        {machineManuals?.map((doc) => (
                          <option key={doc.id || doc._id} value={doc.id || doc._id} className="text-slate-800 py-1 font-normal">
                            {doc.title} {doc.documentType ? `(${doc.documentType})` : ""}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none transition-transform" />
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1.5">
                      AI will cross-reference technician fix steps against this technical manual.
                    </p>
                  </div>

                  {/* AI Note Callout */}
                  <div className="p-3.5 bg-amber-50/70 border border-amber-200/80 rounded-xl flex items-start gap-3">
                    <div className="p-1 rounded-lg bg-amber-100 text-amber-700 shrink-0 mt-0.5">
                      <Info className="w-4 h-4" />
                    </div>
                    <p className="text-xs text-amber-800 leading-relaxed font-medium">
                      AI will analyze the <strong>{stepsCompleted.length} steps</strong> documented by the technician and synthesize them into a formal standard operating procedure.
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 sm:p-5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-end gap-2.5">
                  <button
                    type="button"
                    onClick={() => setIsSopModalOpen(false)}
                    className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 sm:px-4 hover:border-slate-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      generateSopMutation.mutate({
                        logId: id,
                        documentId: selectedManualId,
                      })
                    }
                    disabled={!selectedManualId || generateSopMutation.isPending}
                    className="btn btn-primary text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20 whitespace-nowrap inline-flex items-center gap-1.5 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generateSopMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        Synthesizing Draft...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-1" />
                        Synthesize SOP Draft
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            </div>
          </AnimatePresence>,
          document.body
        )}

      {/* Top Header matching SOPViewer & KnowledgeDetail */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-slate-200/80">
        <div className="flex items-center gap-3 sm:gap-3.5 min-w-0">
          <Link
            to="/maintenance"
            className="p-2 sm:p-2.5 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 border border-slate-200/90 rounded-xl shadow-xs transition-colors shrink-0"
            title="Back to maintenance list"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </Link>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight truncate">
                {log.title}
              </h1>
              {log.work_order_number && (
                <Link
                  to={log.work_order_id ? `/work-orders/${log.work_order_id}` : "/work-orders"}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200/80 hover:bg-blue-100 transition-colors shrink-0"
                  title="View linked work order"
                >
                  <ClipboardList className="w-3 h-3" />
                  WO: {log.work_order_number}
                </Link>
              )}
              <span
                className={clsx(
                  "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border shrink-0",
                  currentStatus.badge
                )}
              >
                <span className={clsx("w-1.5 h-1.5 rounded-full", currentStatus.dot)} />
                <span>{currentStatus.label}</span>
              </span>
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-500 mt-1 truncate">
              <span className="flex items-center gap-1 font-medium text-slate-600">
                <Cpu className="w-3.5 h-3.5 text-slate-400" />
                {log.machine_name || "General Machinery"}
              </span>
              <span className="text-slate-300">•</span>
              <span className="capitalize">{log.maintenance_type || "Standard"}</span>
              {log.created_at && (
                <>
                  <span className="text-slate-300">•</span>
                  <span>Created {formatIstDate(log.created_at)}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap">
          {canManage && !log.work_order_id && !log.work_order_number && log.status !== "completed" && (
            <Link
              to="/work-orders/new"
              state={{
                maintenanceLogId: id,
                machineId: log.machine_id,
                sopId: log.sop_id || "",
                title: log.title,
                description: log.description || "",
                workOrderType: log.maintenance_type || "preventive",
                scheduledStart: log.scheduled_date || "",
              }}
              className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 shadow-xs inline-flex items-center gap-1.5"
            >
              <ClipboardList className="w-4 h-4 text-slate-500" />
              <span>Create Work Order</span>
            </Link>
          )}

          {log.status !== "completed" && (
            <Link
              to={`/maintenance/${id}/execute`}
              className="btn btn-primary text-xs sm:text-sm py-2 px-3.5 sm:px-4 shadow-md shadow-blue-500/20 inline-flex items-center gap-1.5 font-medium whitespace-nowrap"
            >
              {log.status === "in_progress" ? (
                <>
                  <Play className="w-4 h-4" />
                  <span>Continue Execution</span>
                </>
              ) : (
                <>
                  <Wrench className="w-4 h-4" />
                  <span>Start Execution</span>
                </>
              )}
            </Link>
          )}
        </div>
      </div>

      {/* 4-Metric Summary Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <div className="p-4 bg-white border border-slate-200/80 rounded-2xl shadow-xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
            Maintenance Type
          </span>
          <p className="mt-1 text-sm sm:text-base font-bold text-slate-800 capitalize">
            {log.maintenance_type || "Standard"}
          </p>
        </div>

        <div className="p-4 bg-white border border-slate-200/80 rounded-2xl shadow-xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
            Scheduled For
          </span>
          <p className="mt-1 text-sm sm:text-base font-bold text-slate-800">
            {log.scheduled_date ? formatIstDate(log.scheduled_date) : "Immediate"}
          </p>
        </div>

        <div className="p-4 bg-white border border-slate-200/80 rounded-2xl shadow-xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
            Duration
          </span>
          <p className="mt-1 text-sm sm:text-base font-bold text-slate-800">
            {log.duration_minutes != null ? `${log.duration_minutes} mins` : "Not recorded"}
          </p>
        </div>

        <div className="p-4 bg-white border border-slate-200/80 rounded-2xl shadow-xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
            Compliance Score
          </span>
          <p className="mt-1 text-sm sm:text-base font-bold text-slate-800">
            {log.compliance_score != null ? `${log.compliance_score}%` : "Not assessed"}
          </p>
        </div>
      </div>

      {/* Main 12-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 items-start">
        {/* Left Column (8 cols): Objective, Execution Summary, Execution Steps, Document Evidence */}
        <div className="lg:col-span-8 space-y-5 lg:space-y-6">
          {/* Card: Original Objective & Scope */}
          <div className="bg-white border border-slate-200/80 rounded-2xl shadow-xs overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                  <Flag className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900">
                    Original Objective & Scope
                  </h3>
                  <p className="text-xs text-slate-500">
                    Procedure assignment and operational directives
                  </p>
                </div>
              </div>

              {canManage && log.status !== "completed" && !isUpdatingSOP && (
                <button
                  type="button"
                  onClick={() => setIsUpdatingSOP(true)}
                  className="btn btn-secondary text-xs py-1.5 px-3 hover:border-slate-400 inline-flex items-center gap-1.5 shadow-2xs font-medium"
                >
                  <Edit3 className="w-3.5 h-3.5 text-slate-500" />
                  <span>{log.sop_id ? "Change SOP" : "Assign SOP"}</span>
                </button>
              )}
            </div>

            <div className="p-5 space-y-4">
              {isUpdatingSOP ? (
                <div className="bg-slate-50/70 p-4 rounded-xl border border-slate-200/90 space-y-3">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Select Standard Operating Procedure
                  </label>
                  <div className="relative">
                    <BookOpen className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <select
                      className="w-full appearance-none pl-10 pr-10 py-2.5 bg-white hover:bg-slate-50/50 border border-slate-200 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-xs transition-all cursor-pointer"
                      value={selectedSOP}
                      onChange={(e) => setSelectedSOP(e.target.value)}
                    >
                      <option value="" disabled className="text-slate-400">Choose procedure...</option>
                      {availableSOPs?.map((s) => (
                        <option key={s.id} value={s.id} className="text-slate-800 font-normal">
                          {s.title}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => updateSopMutation.mutate(selectedSOP)}
                      disabled={!selectedSOP || updateSopMutation.isPending}
                      className="btn btn-primary text-xs py-1.5 px-3.5 font-medium inline-flex items-center gap-1.5"
                    >
                      {updateSopMutation.isPending && (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      )}
                      <span>Assign SOP</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsUpdatingSOP(false)}
                      className="btn btn-secondary text-xs py-1.5 px-3 hover:border-slate-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-slate-50/70 rounded-xl border border-slate-200/70 text-xs sm:text-sm text-slate-700 leading-relaxed whitespace-pre-wrap font-medium">
                  {log.description || "No primary description provided for this maintenance task."}
                </div>
              )}
            </div>
          </div>

          {/* Execution Summary (AI Analysis) */}
          {executionSummary && (
            <div className="bg-emerald-50/60 border border-emerald-200/80 rounded-2xl p-5 shadow-xs">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <h3 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-emerald-900">
                  Work Performed (AI Summary)
                </h3>
              </div>
              <p className="text-xs sm:text-sm text-slate-700 italic leading-relaxed pl-1">
                "{executionSummary}"
              </p>
              {log.notes && (
                <div className="mt-3.5 pt-3.5 border-t border-emerald-200/60">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 block mb-1">
                    Technician Feedback
                  </label>
                  <p className="text-xs text-slate-600">{log.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Card: Execution Steps & Documentation */}
          <div className="bg-white border border-slate-200/80 rounded-2xl shadow-xs overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                  <ClipboardList className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900">
                    Execution Steps & Documentation
                  </h3>
                  <p className="text-xs text-slate-500">
                    {stepsCompleted.length} steps recorded with technician logs & evidence
                  </p>
                </div>
              </div>
            </div>

            <div className="p-5">
              {stepsCompleted.length > 0 ? (
                <div className="space-y-4">
                  {stepsCompleted.map((step, i) => {
                    const originalInstruction =
                      sopSteps[step.stepIndex]?.instruction ||
                      step.instruction ||
                      `Step ${i + 1}`;
                    return (
                      <div
                        key={i}
                        className="flex items-start gap-3.5 p-3.5 sm:p-4 rounded-xl border border-slate-200/80 bg-slate-50/50 hover:bg-slate-50 transition-colors"
                      >
                        <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center text-xs font-bold border border-blue-100 shrink-0 mt-0.5">
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0 space-y-2">
                          <p className="text-xs sm:text-sm font-semibold text-slate-900 leading-snug">
                            {originalInstruction}
                          </p>
                          <div className="p-3 bg-white border border-slate-200/70 rounded-xl">
                            <p className="text-xs text-slate-700 leading-relaxed">
                              {step.notes || (
                                <span className="italic text-slate-400">
                                  Confirmed complete with no additional notes.
                                </span>
                              )}
                            </p>
                            {(step.photoUrl || step.photo) && (
                              <button
                                type="button"
                                onClick={() =>
                                  setActivePhoto(step.photoUrl || step.photo)
                                }
                                className="mt-2.5 inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-100 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-100 transition-colors"
                              >
                                <Camera className="w-3.5 h-3.5" />
                                <span>View Evidence Photo</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-10 text-slate-400 italic text-xs sm:text-sm">
                  No execution steps recorded yet.
                </div>
              )}
            </div>
          </div>

          {/* Quality / Document Evidence Accordion Card */}
          <div className="bg-white border border-slate-200/80 rounded-2xl shadow-xs p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900">
                    Quality / Document Evidence
                  </h3>
                  <p className="text-xs text-slate-500">
                    {log.documents?.length || 0} attached compliance documents
                  </p>
                </div>
              </div>
            </div>
            {isEvidenceExpanded && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <DocumentEvidenceList documents={log.documents || []} />
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar (4 cols): Knowledge Standardization, AI Plan, Error Bug Reference, Metadata, Parts */}
        <div className="lg:col-span-4 space-y-5 lg:space-y-6">
          {/* Knowledge Capture Banner Card */}
          {showStandardizeButton && (
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-5 text-white shadow-md shadow-blue-500/20 relative overflow-hidden group">
              <FilePlus className="absolute -top-4 -right-4 w-24 h-24 text-white/10 rotate-12 group-hover:scale-110 transition-transform duration-500 pointer-events-none" />
              <div className="relative z-10 space-y-3">
                <div className="flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5 text-blue-200" />
                  <span className="font-bold text-[11px] uppercase tracking-wider text-blue-100">
                    Standardize Knowledge
                  </span>
                </div>
                <p className="text-xs leading-relaxed text-blue-50 font-medium">
                  This corrective fix was performed without a standard procedure. Convert this successful execution into a verified SOP.
                </p>
                <button
                  type="button"
                  onClick={() => setIsSopModalOpen(true)}
                  className="w-full bg-white text-blue-700 py-2.5 px-4 rounded-xl font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-sm hover:bg-blue-50 transition-colors"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Create SOP from Fix</span>
                </button>
              </div>
            </div>
          )}

          {/* AI Suggested Plan Card (from bug analysis) */}
          {log?.bug_id && aiPlan?.suggestion && (
            <div className="bg-slate-900 rounded-2xl p-5 text-white shadow-lg border border-slate-800 relative overflow-hidden">
              <Sparkles className="absolute -top-3 -right-3 w-20 h-20 text-white/10 rotate-12 pointer-events-none" />
              <div className="relative z-10 space-y-3.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-blue-400" />
                    <span className="font-bold text-[11px] uppercase tracking-wider text-blue-300">
                      AI Suggested Plan
                    </span>
                  </div>
                  <span className="bg-blue-500/20 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded-full text-[10px] font-bold">
                    {Math.round((aiPlan.confidence_score || 0) * 100)}% Match
                  </span>
                </div>
                <div className="text-xs leading-relaxed text-slate-200 font-medium whitespace-pre-wrap border-l-2 border-blue-500/40 pl-3 py-0.5">
                  {aiPlan.suggestion}
                </div>
                {aiPlan.references?.length > 0 && (
                  <div className="space-y-1.5 pt-3 border-t border-white/10">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Evidence Sources
                    </p>
                    <div className="space-y-1">
                      {aiPlan.references.map((ref, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 bg-white/5 p-2 rounded-lg"
                        >
                          {ref.type === "manual" ? (
                            <FileText className="w-3 h-3 text-blue-400 shrink-0" />
                          ) : (
                            <History className="w-3 h-3 text-amber-400 shrink-0" />
                          )}
                          <span className="text-[11px] font-medium text-slate-300 truncate">
                            {ref.title}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Linked Bug / Error Log Card */}
          {linkedBug && (
            <div className="bg-white border border-slate-200/80 rounded-2xl shadow-xs overflow-hidden">
              <div className="p-3.5 bg-rose-50/70 border-b border-rose-100 flex items-center justify-between text-rose-800">
                <div className="flex items-center gap-1.5 font-bold text-[11px] uppercase tracking-wider">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  <span>Linked Error Alert</span>
                </div>
                <span className="bg-rose-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">
                  Ref: {log.bug_id.substring(0, 6)}
                </span>
              </div>
              <div className="p-4 space-y-3">
                <p className="text-xs text-slate-700 font-medium leading-relaxed italic">
                  "{linkedBug.description || linkedBug.message || linkedBug.title || "No alert details available."}"
                </p>
                {linkedBug.mediaUrls?.length > 0 && (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    {linkedBug.mediaUrls.map((url, idx) => (
                      <div
                        key={idx}
                        onClick={() => setActivePhoto(url)}
                        className="aspect-square rounded-xl overflow-hidden border border-slate-200 cursor-zoom-in hover:opacity-90 transition-opacity"
                      >
                        <img
                          src={url}
                          className="w-full h-full object-cover"
                          alt="Bug Evidence"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Metadata Card matching SOPViewer & KnowledgeDetail */}
          <div className="bg-white border border-slate-200/80 rounded-2xl shadow-xs p-5 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                  Task Metadata
                </h3>
              </div>
              <span
                className={clsx(
                  "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border",
                  currentStatus.badge
                )}
              >
                <span className={clsx("w-1.5 h-1.5 rounded-full", currentStatus.dot)} />
                <span>{currentStatus.label}</span>
              </span>
            </div>

            <div className="divide-y divide-slate-100 text-xs">
              {/* Machine Unit */}
              <div className="flex justify-between items-center py-2.5 gap-2">
                <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                  <Cpu className="w-3.5 h-3.5 text-slate-400" /> Machine
                </span>
                <span className="font-semibold text-slate-800 truncate max-w-[160px]">
                  {log.machine_name || "Unassigned"}
                </span>
              </div>

              {/* Procedure */}
              <div className="flex justify-between items-center py-2.5 gap-2">
                <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                  <FileText className="w-3.5 h-3.5 text-slate-400" /> Procedure
                </span>
                <span className="font-semibold text-slate-800 truncate max-w-[160px]">
                  {log.sop_title || "General Maintenance"}
                </span>
              </div>

              {/* Assigned Technician */}
              <div className="flex justify-between items-center py-2.5 gap-2">
                <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                  <User className="w-3.5 h-3.5 text-slate-400" /> Assigned To
                </span>
                {isAssigning ? (
                  <div className="flex items-center gap-1.5">
                    <div className="relative">
                      <select
                        className="text-xs appearance-none pl-2.5 pr-7 py-1.5 bg-white border border-slate-200 focus:border-blue-500 rounded-lg outline-none focus:ring-2 focus:ring-blue-500/20 font-medium text-slate-800 shadow-2xs transition-all cursor-pointer"
                        value={selectedTech}
                        onChange={(e) => setSelectedTech(e.target.value)}
                      >
                        <option value="" disabled className="text-slate-400">Choose...</option>
                        {technicians?.map((tech) => (
                          <option key={tech.id} value={tech.id} className="text-slate-800">
                            {tech.firstName} {tech.lastName}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                    </div>
                    <button
                      type="button"
                      onClick={() => assignMutation.mutate(selectedTech)}
                      disabled={!selectedTech || assignMutation.isPending}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      {assignMutation.isPending ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Save className="w-3.5 h-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsAssigning(false)}
                      className="p-1 text-slate-400 hover:bg-slate-100 rounded"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <span className="font-semibold text-slate-800">
                      {log.technician_name || "Unassigned"}
                    </span>
                    {canManage && log.status !== "completed" && (
                      <button
                        type="button"
                        onClick={() => setIsAssigning(true)}
                        className="text-[11px] font-semibold text-blue-600 hover:underline ml-1"
                      >
                        ({log.technician_id ? "Reassign" : "Assign"})
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Started At */}
              <div className="flex justify-between items-center py-2.5 gap-2">
                <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                  <Clock className="w-3.5 h-3.5 text-slate-400" /> Started
                </span>
                <span className="font-semibold text-slate-800">
                  {log.started_at ? formatIstDateTime(log.started_at) : "Not started"}
                </span>
              </div>

              {/* Completed At */}
              <div className="flex justify-between items-center py-2.5 gap-2">
                <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5 text-slate-400" /> Completed
                </span>
                <span className="font-semibold text-slate-800">
                  {log.completed_at ? formatIstDateTime(log.completed_at) : "In progress"}
                </span>
              </div>
            </div>
          </div>

          {/* Parts Identification Card */}
          <div className="bg-white border border-slate-200/80 rounded-2xl shadow-xs p-5">
            <div className="flex items-center gap-2 mb-3.5">
              <Wrench className="w-4 h-4 text-slate-400" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                Parts Consumed
              </h3>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {partsUsed.length > 0 ? (
                partsUsed.map((part, i) => (
                  <span
                    key={i}
                    className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold border border-blue-100"
                  >
                    {part}
                  </span>
                ))
              ) : (
                <span className="text-slate-400 text-xs italic">
                  No replacement parts consumed.
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceDetail;
